import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, MessageSquare, ListTodo, CheckCircle, Clock } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { NotificationList } from './NotificationList';
import { NotificationBadge } from './NotificationBadge';
import { format } from 'date-fns';

interface NotificationsDropdownProps {
  placement?: 'bottom-end' | 'bottom-start';
}

export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ 
  placement = 'bottom-end' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { messages, tasks, currentUser } = useStore();
  
  // Get unread messages and tasks
  const unreadMessages = messages.filter(msg => 
    msg.sender.id !== currentUser?.id && // Exclude messages sent by current user
    !msg.acknowledgments.includes(currentUser?.id || '') && 
    (msg.departments.includes(currentUser?.department || '') || 
     msg.recipients?.some(r => r.id === currentUser?.id))
  );
  
  const unreadTasks = tasks.filter(task => 
    task.assignedTo === currentUser?.id && // Only tasks assigned to current user
    task.assignedBy !== currentUser?.id && // Exclude tasks created by current user
    !task.acknowledged
  );
  
  const unreadCount = unreadMessages.length + unreadTasks.length;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.notifications-dropdown')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Lock body scroll when mobile dropdown is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleMarkAsRead = (notificationId: string) => {
    // Handle marking as read
    if (unreadCount === 0) {
      setIsOpen(false);
    }
  };

  const placementClasses = {
    'bottom-end': 'right-0',
    'bottom-start': 'left-0'
  };

  return (
    <div className="relative notifications-dropdown">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
      >
        <Bell className="w-5 h-5" />
        <NotificationBadge count={unreadCount} />
      </Button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Mobile backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/20 lg:hidden"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown container */}
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className={`
                fixed lg:absolute 
                z-50
                lg:mt-2
                ${placementClasses[placement]}
                lg:w-96
                w-[calc(100vw-2rem)]
                max-h-[calc(100vh-5rem)]
                mx-4
                lg:mx-0
                top-16
                lg:top-auto
                notifications-dropdown-content
              `}
            >
              <Card className="overflow-hidden shadow-xl border border-gray-200">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-gray-500" />
                    <h3 className="font-semibold text-gray-900">Activity</h3>
                    {unreadCount > 0 && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOpen(false)}
                    className="lg:hidden"
                    icon={<X className="w-4 h-4" />}
                  />
                </div>

                {/* Notification list with scroll */}
                <div className="max-h-[calc(100vh-10rem)] overflow-y-auto overscroll-contain divide-y divide-gray-100">
                  {/* Unread Messages */}
                  {unreadMessages.length > 0 && (
                    <div className="p-4 space-y-4">
                      <h4 className="text-sm font-medium text-gray-700">Messages</h4>
                      {unreadMessages.map(msg => (
                        <div key={msg.id} className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <MessageSquare className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900">{msg.title}</p>
                            <p className="text-sm text-gray-600 truncate">{msg.content}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              From {msg.sender.name} • {format(new Date(msg.createdAt), 'MMM d, h:mm a')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Unread Tasks */}
                  {unreadTasks.length > 0 && (
                    <div className="p-4 space-y-4">
                      <h4 className="text-sm font-medium text-gray-700">Tasks</h4>
                      {unreadTasks.map(task => (
                        <div key={task.id} className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                          <div className="p-2 bg-emerald-100 rounded-lg">
                            <ListTodo className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900">{task.title}</p>
                            <p className="text-sm text-gray-600 truncate">{task.description}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                Due {format(new Date(task.dueDate), 'MMM d, h:mm a')}
                              </span>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                task.priority === 'URGENT' 
                                  ? 'bg-red-100 text-red-700'
                                  : task.priority === 'HIGH'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {task.priority}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {unreadCount === 0 && (
                    <div className="p-8 text-center text-gray-500">
                      No new notifications
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};